import { useEffect, useMemo, useState } from 'react';

import { useRelayEnvironment } from 'react-relay/hooks';

import { getUsersLoader, type HydratedUser } from '../utils/getUsersLoader';

import { useStableArray } from './useStableArray';

export type HydratedUserResult = {
	// The account ID or ARI of the user
	accountIdOrAri: string;
	error: Error | null;
	id?: string;
	status: 'INIT' | 'LOADING' | 'READY' | 'ERRORED';
	user: HydratedUser | null;
};

export function useHydratedUsers({
	accountIds,
	shouldHydrate,
}: {
	accountIds: string[];
	shouldHydrate: boolean;
}): HydratedUserResult[] {
	const environment = useRelayEnvironment();
	const loader = getUsersLoader(environment);

	const accountIdsToFetch = useStableArray(accountIds, { sort: true, deduplicate: true });

	// Initialize state
	const [results, setResults] = useState<Record<string, HydratedUserResult>>({});

	useEffect(() => {
		if (!shouldHydrate || accountIdsToFetch.length === 0) {
			return;
		}

		// 1. Optimistically update status to IN_FLIGHT for missing items
		setResults((prev) => {
			const next = { ...prev };
			let dirty = false;

			accountIdsToFetch.forEach((accountId) => {
				// If we don't have a DONE status for this ID, mark it as IN_FLIGHT
				if (next[accountId]?.status !== 'READY') {
					next[accountId] = {
						user: next[accountId]?.user || null,
						status: 'LOADING',
						error: null,
						accountIdOrAri: accountId,
						id: accountId,
					};
					dirty = true;
				}
			});

			return dirty ? next : prev;
		});

		// 2. Delegate to the Loader
		Promise.all(
			accountIdsToFetch.map(async (accountId) => {
				try {
					const user = await loader?.load(accountId);
					return { accountId, user, error: null };
				} catch (e) {
					return { accountId, user: null, error: e };
				}
			}),
		).then((fetchedData) => {
			setResults((prev) => {
				const next = { ...prev };
				const fetchedDataMap = new Map(fetchedData.map((data) => [data.accountId, data]));
				accountIdsToFetch.forEach((accountId) => {
					const data = fetchedDataMap.get(accountId);
					if (data && data.user) {
						next[accountId] = {
							accountIdOrAri: accountId,
							id: accountId,
							user: data.user,
							status: 'READY',
							error: null,
						};
					} else {
						next[accountId] = {
							accountIdOrAri: accountId,
							id: accountId,
							user: null,
							status: 'ERRORED',
							error: shouldHydrate ? new Error(`Failed to hydrate user ${accountId}`) : null,
						};
					}
				});
				return next;
			});
		});
	}, [accountIdsToFetch, shouldHydrate, loader]);

	return useMemo(
		() =>
			accountIdsToFetch.map(
				(accountId) =>
					results[accountId] ?? {
						accountIdOrAri: accountId,
						user: null,
						status: 'ERRORED',
						error: new Error(`Unknown error. Failed to hydrate user ${accountId}`),
					},
			),
		[accountIdsToFetch, results],
	);
}
